import {
	Component,
	forwardRef,
	AfterViewInit,
	ViewChild,
	Input,
	OnInit,
} from '@angular/core';
import { Contact } from 'src/shared/api';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormGroup,
	FormArray,
	FormBuilder,
	AbstractControl,
} from '@angular/forms';
import { contactFormConfig } from 'src/shared/form-models/contact/contact.component';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AbstractControlDataSource } from 'src/shared/form-models/abstract-control-data-source';
import { takeUntil, filter } from 'rxjs/operators';
import { ContactDialogComponent } from 'src/shared/form-models/contact/contact-dialog/contact-dialog.component';
import { animateList } from 'src/shared/animations';
import { FormConfig } from '../../form-cva.component';

/*
	This table component is built using a form array to allow for easier editing.
	The important thing to remember is that the "parent" FormGroup is just a wrapper
	and has no actual value outside of giving us somewhere to store a FormArray. As of
	Angular 7 (and probably 8), there is no way to have a FormArray be the top level
	of a form.
*/

@Component({
	selector: 'rpshared-contact-list',
	templateUrl: './contact-list.component.html',
	styleUrls: ['./contact-list.component.scss'],
	animations: [animateList],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ContactListComponent),
			multi: true,
		},
	],
})
export class ContactListComponent extends SelfDestruct
	implements ControlValueAccessor, OnInit, AfterViewInit {
	@Input() showRequiredError = false;
	@Input() emailRequired = true;
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	emptyColumns = ['empty'];
	resultColumns = ['lastName', 'phone', 'email', 'edit', 'delete'];
	formConfig: FormConfig<Required<Contact>> = { ...contactFormConfig };
	// Form Controls
	parent = new FormGroup({
		array: new FormArray([]),
	});

	dataSource = new AbstractControlDataSource<Contact>(this.array);

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog
	) {
		super();
	}

	ngOnInit() {
		if(this.emailRequired === false) {
			this.formConfig.emailAddresses = [[]];
		}
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	// model --> view
	writeValue(value: Contact[]): void {
		if (value) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(this.formConfig) as FormGroup;
				fc.setValue(v);
				this.array.push(fc);
			});
		}
	}

	// view --> model
	registerOnChange(fn: (value: Contact[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addContact() {
		var matDialogRef = this.dialog
			.open<ContactDialogComponent, never, FormGroup>(
				ContactDialogComponent,
				{ width: '450px' }
			);
			matDialogRef.componentInstance.emailRequired = this.emailRequired;
			matDialogRef.afterClosed()
			.pipe(filter<AbstractControl>(Boolean))
			.subscribe((control) => {
				this.array.insert(0, control);
			});
	}

	editContact(contact: Contact) {
		var matDialogRef = this.dialog
			.open<ContactDialogComponent, Contact, FormGroup>(
				ContactDialogComponent,
				{
					width: '450px',
					data: contact
				}
			);
		
		matDialogRef.componentInstance.emailRequired = this.emailRequired;
		matDialogRef.afterClosed()
			.pipe(filter<AbstractControl>(Boolean))
			.subscribe((control) => {
				this.array.setControl(this.findContactIndex(contact), control);
			});
	}

	findContactIndex(contact: Contact){
		return this.dataSource.data.findIndex(c => c === contact);
	}

	get array() {
		return this.parent.get('array') as FormArray;
	}
}
